import { ColDef, ValueGetterParams } from "ag-grid-community";

import { NLISRelatedTransaction } from "lib/agGrid/columns/nlisFile";
import { ACTION_COLUMN_ID, ACTION_COLUMN_NAME } from "constants/aggrid";
import { actionsColumnRenderer } from "containers/NLISFiles/ActionColumn";

import { ColumnType } from "components/AgGrid/constants";
import { NLISFileTypeHeadings } from "constants/nlis";
import { caseInsensitiveCompare } from "../../lib/compare";

export interface NlisFileRelatedTransaction {
  id: string;
  transactionId: string;
  transferType: string;
}

export interface NlisFileRow {
  buyerDisplay?: string | null;
  created?: string | null;
  eidCount: number;
  eidStatus?: string | null;
  eidSuccessCount: number;
  id: string;
  processed?: string | null;
  relatedTransaction?: NlisFileRelatedTransaction | null;
  status?: string | null;
  submitted?: string | null;
  transactionId?: string | null;
  transferType: string;
  vendorDisplay?: string | null;
}

export const NlisFileColId = {
  TRANSFER_TYPE: "0275c35e-7806-4dfa-b3be-cc66f65297bd",
  TRANSACTION_ID: "ae658876-3141-4ee9-8649-6243c95e573f",
  STATUS: "6d9ddc79-15ad-413b-ab32-903bb70e29b4",
  EID_STATUS: "d29fdd0d-31c3-4ee6-b8f5-e52012f0c0ac",
  VENDOR_DISPLAY: "9bcd0153-fe29-49a6-b9d5-cb4ef2b721ea",
  BUYER_DISPLAY: "ddbe63a3-4419-4842-a0e1-e101dc6560c0",
  CREATED: "381bd8fa-c8c4-4349-8524-cdbc9c60659f",
  SUBMITTED: "8cb94cda-316c-4433-b898-8f729691a5f3",
  PROCESSED: "dd590f74-6264-4617-b494-5cbb8b80581b",
  RELATED_TRANSACTION: "f8f4a2f7-5728-43ae-9964-b4a96cba8624",
} as const;

export function eidStatusGetter({
  data,
}: ValueGetterParams<NlisFileRow>): string {
  if (!data) {
    return "";
  }
  const { eidSuccessCount, eidCount } = data;
  return `${eidSuccessCount} / ${eidCount} Successful`;
}

export function nlisFileTypeGetter({
  data,
}: ValueGetterParams<NlisFileRow>): string {
  if (!data) {
    return "";
  }
  const { transferType } = data;
  return NLISFileTypeHeadings[transferType] || transferType;
}

export const NlisFileColumnDefinitions: Record<string, ColDef<NlisFileRow>> = {
  [ACTION_COLUMN_ID]: {
    headerName: ACTION_COLUMN_NAME,
    colId: ACTION_COLUMN_ID,
    cellRenderer: actionsColumnRenderer,
    pinned: "right",
    width: 100,
  },
  [NlisFileColId.BUYER_DISPLAY]: {
    headerName: "Buyer",
    colId: NlisFileColId.BUYER_DISPLAY,
    field: "buyerDisplay",
    enableRowGroup: true,
  },
  [NlisFileColId.CREATED]: {
    headerName: "Created",
    colId: NlisFileColId.CREATED,
    field: "created",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  [NlisFileColId.EID_STATUS]: {
    headerName: "EID Status",
    colId: NlisFileColId.EID_STATUS,
    field: "eidStatus",
    valueGetter: eidStatusGetter,
    comparator: caseInsensitiveCompare,
  },
  [NlisFileColId.PROCESSED]: {
    headerName: "Processed",
    colId: NlisFileColId.PROCESSED,
    field: "processed",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },

  [NlisFileColId.RELATED_TRANSACTION]: {
    headerName: "Related Transaction",
    colId: NlisFileColId.RELATED_TRANSACTION,
    field: "relatedTransaction",
    cellRenderer: NLISRelatedTransaction,
  },
  [NlisFileColId.STATUS]: {
    headerName: "Upload Status",
    colId: NlisFileColId.STATUS,
    field: "status",
    enableRowGroup: true,
  },
  [NlisFileColId.SUBMITTED]: {
    headerName: "Submitted",
    colId: NlisFileColId.SUBMITTED,
    field: "submitted",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  [NlisFileColId.TRANSACTION_ID]: {
    headerName: "Transaction Id",
    colId: NlisFileColId.TRANSACTION_ID,
    field: "transactionId",
    type: ColumnType.NUMERIC,
  },
  [NlisFileColId.TRANSFER_TYPE]: {
    headerName: "Type",
    colId: NlisFileColId.TRANSFER_TYPE,
    field: "transferType",
    valueGetter: nlisFileTypeGetter,
    enableRowGroup: true,
  },
  [NlisFileColId.VENDOR_DISPLAY]: {
    headerName: "Consignment",
    colId: NlisFileColId.VENDOR_DISPLAY,
    field: "vendorDisplay",
    enableRowGroup: true,
  },
};
